import React from 'react';
import { Switch } from '@rebass/forms';
import classNames from 'classnames';

import { useAuth } from 'context/AuthContext';
import { useToastHelper } from 'hooks/useToastHelper';
import { useUpdateAccountSettingsMutation } from 'api/useUpdateAccountSettingsMutation';
import services from 'services/services';
import auth from 'services/auth';

/**
 * @typedef {Object} UniquePhoneNumberSwitchProps
 * @property {string} [className]
 * @property {string} [id]
 */

/**
 * Toggle for the account-level unique phone number enforcement.
 *
 * When enabled, panelists cannot register with a phone number
 * already used by another panelist in the account.
 *
 * @param {UniquePhoneNumberSwitchProps & React.InputHTMLAttributes<HTMLInputElement>} props
 */
export function UniquePhoneNumberSwitch({ className, ...rest }) {
    const authContext = useAuth();
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const isEnabled = !!authContext?.account?.settings?.is_unique_phone_number_enabled;

    const { mutate, isLoading } = useUpdateAccountSettingsMutation({
        onSuccess: () => {
            const newValue = !isEnabled;
            authContext.account.settings.is_unique_phone_number_enabled = newValue;
            auth.checkAuth();
            addSuccessToast(
                newValue ? 'Unique phone number enforcement enabled' : 'Unique phone number enforcement disabled'
            );
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });

    return (
        <Switch
            role="switch"
            aria-checked={isEnabled}
            aria-label="Enforce unique phone numbers"
            className={classNames('switch-small', className)}
            checked={isEnabled}
            disabled={isLoading}
            onClick={() => mutate({ is_unique_phone_number_enabled: !isEnabled })}
            {...rest}
        />
    );
}
